import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class FSchddService {

  constructor(private http: HttpClient) { }

  /**
   * Fetch Schedule D details
   * @param baseUrl Base URL for API calls
   * @param rowData Row data
   * @param sso_id SSO ID
   * @param scenario Scenario
   * @param globalService Global service data (optional)
   * @param filterParams Filter parameters (optional)
   * @returns Observable with Schedule D details
   */
  fetchSchddDetails(
    baseUrl: string, 
    rowData: any, 
    sso_id: string, 
    scenario: string,
    globalService: any = {},
    filterParams: any = {}
  ): Observable<any> {
    // Build params from all sources with proper fallbacks (no hardcoded values)
    const params: { [key: string]: any } = {};
    
    // Action code (required)
    params.action_code = 'll3gc9';
    
    // SSO ID
    const ssoId = sso_id || 
                  filterParams.sso_id || 
                  rowData.sso_id || 
                  globalService?.sso_id || 
                  globalService?.userSettingsData?.user?.sso_id ||
                  globalService?.globalParams?.sso_id;
    if (ssoId !== undefined && ssoId !== null && ssoId !== '') {
      params.sso_id = ssoId;
    }
    
    // Tax year
    const taxYear = filterParams.tax_year || 
                    rowData.TAX_YEAR || 
                    globalService?.tax_year || 
                    globalService?.globalParams?.tax_year;
    if (taxYear !== undefined && taxYear !== null && taxYear !== '') {
      params.tax_year = taxYear;
    }
    
    // Scenario
    const scenarioValue = scenario || 
                          filterParams.scenario || 
                          rowData.SCENARIO || 
                          globalService?.scenario || 
                          globalService?.globalParams?.scenario;
    if (scenarioValue !== undefined && scenarioValue !== null && scenarioValue !== '') {
      params.scenario = scenarioValue;
    }
    
    // JCD key
    const jcdKey = filterParams.jcd_key || 
                   rowData.JCD_KEY || 
                   globalService?.jcd_key || 
                   globalService?.globalParams?.jcd_key;
    if (jcdKey !== undefined && jcdKey !== null && jcdKey !== '') {
      params.jcd_key = jcdKey;
    } else {
      params.jcd_key = 250; // Default fallback as per AngularJS
    }
    
    // Combination key
    const combinationKey = filterParams.combination_key || 
                           rowData.COMBINATION_KEY || 
                           globalService?.combination_key || 
                           globalService?.globalParams?.combination_key;
    if (combinationKey !== undefined && combinationKey !== null && combinationKey !== '') {
      params.combination_key = combinationKey;
    }
    
    // JCD TA key
    const jcdTaKey = filterParams.jcd_ta_key || 
                     rowData.jcd_ta_key || 
                     globalService?.jcd_ta_key || 
                     globalService?.globalParams?.jcd_ta_key;
    if (jcdTaKey !== undefined && jcdTaKey !== null && jcdTaKey !== '') {
      params.jcd_ta_key = jcdTaKey;
    }
    
    // Activity key
    const activityKey = filterParams.activity_key || 
                        rowData.activity_key || 
                        globalService?.activity_key || 
                        globalService?.globalParams?.activity_key;
    if (activityKey !== undefined && activityKey !== null && activityKey !== '') {
      params.activity_key = activityKey;
    }
    
    // Filing key
    const filingKey = filterParams.filing_key || 
                      rowData.filing_key || 
                      globalService?.filing_key || 
                      globalService?.globalParams?.filing_key;
    if (filingKey !== undefined && filingKey !== null && filingKey !== '') {
      params.filing_key = filingKey;
    }
    
    // Filter by
    const filterBy = filterParams.filter_by || 
                     rowData.filter_by || 
                     globalService?.filter_by || 
                     globalService?.globalParams?.filter_by;
    if (filterBy !== undefined && filterBy !== null && filterBy !== '') {
      params.filter_by = filterBy;
    }
    
    // Is issue key enabled
    const isIssueKeyEnabled = filterParams.is_issue_key_enabled || 
                              rowData.is_issue_key_enabled || 
                              globalService?.is_issue_key_enabled || 
                              globalService?.globalParams?.is_issue_key_enabled;
    if (isIssueKeyEnabled !== undefined && isIssueKeyEnabled !== null && isIssueKeyEnabled !== '') {
      params.is_issue_key_enabled = isIssueKeyEnabled;
    }
    
    // Is locked
    const isLocked = filterParams.is_locked || 
                     rowData.is_locked || 
                     globalService?.is_locked || 
                     globalService?.globalParams?.is_locked;
    if (isLocked !== undefined && isLocked !== null && isLocked !== '') {
      params.is_locked = isLocked;
    }
    
    // Issue ID (can be empty string)
    const issueId = filterParams.issue_id || 
                    rowData.ISSUE_KEY || 
                    rowData.issue_id || 
                    globalService?.issue_id || 
                    globalService?.globalParams?.issue_id ||
                    '';
    params.issue_id = issueId !== undefined && issueId !== null ? issueId.toString() : '';
    
    // Issue ID for TX save (can be empty string)
    const issueIdForTxSave = filterParams.issue_id_for_tx_save || 
                             rowData.issue_id_for_tx_save || 
                             globalService?.issue_id_for_tx_save || 
                             globalService?.globalParams?.issue_id_for_tx_save ||
                             '';
    params.issue_id_for_tx_save = issueIdForTxSave !== undefined && issueIdForTxSave !== null ? issueIdForTxSave.toString() : '';
    
    // Issue short desc (can be empty string)
    const issueShortDesc = filterParams.issue_short_desc || 
                           rowData.issue_short_desc || 
                           globalService?.issue_short_desc || 
                           globalService?.globalParams?.issue_short_desc ||
                           '';
    params.issue_short_desc = issueShortDesc !== undefined && issueShortDesc !== null ? issueShortDesc.toString() : '';
    
    // Issue key
    const issueKey = filterParams.issue_key || 
                     rowData.ISSUE_KEY || 
                     rowData.issue_key || 
                     globalService?.issue_key || 
                     globalService?.globalParams?.issue_key;
    if (issueKey !== undefined && issueKey !== null && issueKey !== '') {
      params.issue_key = issueKey;
    } else {
      params.issue_key = 0; // Default to 0 if not found
    }
    
    // Scenario code
    const scenarioCode = filterParams.scenarioCode || 
                         filterParams.scenario_code || 
                         rowData.scenarioCode || 
                         rowData.scenario_code || 
                         globalService?.scenarioCode || 
                         globalService?.globalParams?.scenarioCode;
    if (scenarioCode !== undefined && scenarioCode !== null && scenarioCode !== '') {
      params.scenarioCode = scenarioCode;
    }
    
    // Screen key
    const screenKey = filterParams.screen_key || 
                      rowData.screen_key || 
                      globalService?.screen_key || 
                      globalService?.globalParams?.screen_key;
    if (screenKey !== undefined && screenKey !== null && screenKey !== '') {
      params.screen_key = screenKey;
    }
    
    // Screen type
    const screenType = filterParams.screen_type || 
                       rowData.screen_type || 
                       globalService?.screen_type || 
                       globalService?.globalParams?.screen_type;
    if (screenType !== undefined && screenType !== null && screenType !== '') {
      params.screen_type = screenType;
    }
    
    // Parent ME string
    const parentMeString = filterParams.parent_me_string || 
                           rowData.parent_me_string || 
                           globalService?.parent_me_string || 
                           globalService?.globalParams?.parent_me_string;
    if (parentMeString !== undefined && parentMeString !== null && parentMeString !== '') {
      params.parent_me_string = parentMeString;
    }

    // Build query string parameters for GET request (matching beat-type-consol-add pattern)
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      // Include empty strings for issue-related fields as they're required in payload
      if (key === 'issue_id' || key === 'issue_short_desc' || key === 'issue_id_for_tx_save') {
        queryParams.set(key, params[key].toString());
      } else if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.set(key, params[key].toString());
      }
    });

    // Use GET request with query string parameters (matching beat-type-consol-add pattern)
    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Fetch Category Code dropdown values
   * @param baseUrl Base URL for API calls
   * @param rowData Row data
   * @param sso_id SSO ID
   * @param globalService Global service data (optional)
   * @param filterParams Filter parameters (optional)
   * @returns Observable with category code dropdown values
   */
  fetchCategoryCodeDropdownValues(
    baseUrl: string, 
    rowData: any, 
    sso_id: string,
    globalService: any = {},
    filterParams: any = {}
  ): Observable<any> {
    // Build params from all sources with proper fallbacks (no hardcoded values)
    const params: { [key: string]: any } = {};
    
    // Action code (required)
    params.action_code = 'dg64rm';
    
    // SSO ID
    const ssoId = sso_id || 
                  filterParams.sso_id || 
                  rowData.sso_id || 
                  globalService?.sso_id || 
                  globalService?.userSettingsData?.user?.sso_id ||
                  globalService?.globalParams?.sso_id;
    if (ssoId !== undefined && ssoId !== null && ssoId !== '') {
      params.sso_id = ssoId;
    }
    
    // Tax year
    const taxYear = filterParams.tax_year || 
                    rowData.TAX_YEAR || 
                    globalService?.tax_year || 
                    globalService?.globalParams?.tax_year;
    if (taxYear !== undefined && taxYear !== null && taxYear !== '') {
      params.tax_year = taxYear;
    }
    
    // Scenario
    const scenario = filterParams.scenario || 
                     rowData.SCENARIO || 
                     globalService?.scenario || 
                     globalService?.globalParams?.scenario;
    if (scenario !== undefined && scenario !== null && scenario !== '') {
      params.scenario = scenario;
    }
    
    // JCD key
    const jcdKey = filterParams.jcd_key || 
                   rowData.JCD_KEY || 
                   globalService?.jcd_key || 
                   globalService?.globalParams?.jcd_key;
    if (jcdKey !== undefined && jcdKey !== null && jcdKey !== '') {
      params.jcd_key = jcdKey;
    } else {
      params.jcd_key = 250; // Default fallback as per AngularJS
    }
    
    // JCD TA key
    const jcdTaKey = filterParams.jcd_ta_key || 
                     rowData.jcd_ta_key || 
                     globalService?.jcd_ta_key || 
                     globalService?.globalParams?.jcd_ta_key;
    if (jcdTaKey !== undefined && jcdTaKey !== null && jcdTaKey !== '') {
      params.jcd_ta_key = jcdTaKey;
    }
    
    // Activity key
    const activityKey = filterParams.activity_key || 
                        rowData.activity_key || 
                        globalService?.activity_key || 
                        globalService?.globalParams?.activity_key;
    if (activityKey !== undefined && activityKey !== null && activityKey !== '') {
      params.activity_key = activityKey;
    }
    
    // Filing key
    const filingKey = filterParams.filing_key || 
                      rowData.filing_key || 
                      globalService?.filing_key || 
                      globalService?.globalParams?.filing_key;
    if (filingKey !== undefined && filingKey !== null && filingKey !== '') {
      params.filing_key = filingKey;
    }
    
    // Filter by
    const filterBy = filterParams.filter_by || 
                     rowData.filter_by || 
                     globalService?.filter_by || 
                     globalService?.globalParams?.filter_by;
    if (filterBy !== undefined && filterBy !== null && filterBy !== '') {
      params.filter_by = filterBy;
    }
    
    // Is issue key enabled
    const isIssueKeyEnabled = filterParams.is_issue_key_enabled || 
                              rowData.is_issue_key_enabled || 
                              globalService?.is_issue_key_enabled || 
                              globalService?.globalParams?.is_issue_key_enabled;
    if (isIssueKeyEnabled !== undefined && isIssueKeyEnabled !== null && isIssueKeyEnabled !== '') {
      params.is_issue_key_enabled = isIssueKeyEnabled;
    }
    
    // Is locked
    const isLocked = filterParams.is_locked || 
                     rowData.is_locked || 
                     globalService?.is_locked || 
                     globalService?.globalParams?.is_locked;
    if (isLocked !== undefined && isLocked !== null && isLocked !== '') {
      params.is_locked = isLocked;
    }
    
    // Issue ID (can be empty string)
    const issueId = filterParams.issue_id || 
                    rowData.ISSUE_KEY || 
                    rowData.issue_id || 
                    globalService?.issue_id || 
                    globalService?.globalParams?.issue_id ||
                    '';
    params.issue_id = issueId !== undefined && issueId !== null ? issueId.toString() : '';
    
    // Issue ID for TX save (can be empty string)
    const issueIdForTxSave = filterParams.issue_id_for_tx_save || 
                             rowData.issue_id_for_tx_save || 
                             globalService?.issue_id_for_tx_save || 
                             globalService?.globalParams?.issue_id_for_tx_save ||
                             '';
    params.issue_id_for_tx_save = issueIdForTxSave !== undefined && issueIdForTxSave !== null ? issueIdForTxSave.toString() : '';
    
    // Issue short desc (can be empty string)
    const issueShortDesc = filterParams.issue_short_desc || 
                           rowData.issue_short_desc || 
                           globalService?.issue_short_desc || 
                           globalService?.globalParams?.issue_short_desc ||
                           '';
    params.issue_short_desc = issueShortDesc !== undefined && issueShortDesc !== null ? issueShortDesc.toString() : '';
    
    // Scenario code
    const scenarioCode = filterParams.scenarioCode || 
                         filterParams.scenario_code || 
                         rowData.scenarioCode || 
                         rowData.scenario_code || 
                         globalService?.scenarioCode || 
                         globalService?.globalParams?.scenarioCode;
    if (scenarioCode !== undefined && scenarioCode !== null && scenarioCode !== '') {
      params.scenarioCode = scenarioCode;
    }
    
    // Screen key
    const screenKey = filterParams.screen_key || 
                      rowData.screen_key || 
                      globalService?.screen_key || 
                      globalService?.globalParams?.screen_key;
    if (screenKey !== undefined && screenKey !== null && screenKey !== '') {
      params.screen_key = screenKey;
    }
    
    // Screen type
    const screenType = filterParams.screen_type || 
                       rowData.screen_type || 
                       globalService?.screen_type || 
                       globalService?.globalParams?.screen_type;
    if (screenType !== undefined && screenType !== null && screenType !== '') {
      params.screen_type = screenType;
    }
    
    // Parent ME string
    const parentMeString = filterParams.parent_me_string || 
                           rowData.parent_me_string || 
                           globalService?.parent_me_string || 
                           globalService?.globalParams?.parent_me_string;
    if (parentMeString !== undefined && parentMeString !== null && parentMeString !== '') {
      params.parent_me_string = parentMeString;
    }

    // Build query string parameters for GET request (matching beat-type-consol-add pattern)
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      // Include empty strings for issue-related fields as they're required in payload
      if (key === 'issue_id' || key === 'issue_short_desc' || key === 'issue_id_for_tx_save') {
        queryParams.set(key, params[key].toString());
      } else if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.set(key, params[key].toString());
      }
    });

    // Use GET request with query string parameters (matching beat-type-consol-add pattern)
    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Delete Reportable Transaction
   * @param baseUrl Base URL for API calls
   * @param rowData Row data
   * @param trans_details_key Transaction details key
   * @param occurrence Occurrence
   * @param line_no Line number
   * @param scenario Scenario
   * @param globalService Global service data
   * @param filterParams Filter parameters
   * @returns Observable with response
   */
  deleteRepTransaction(
    baseUrl: string,
    rowData: any,
    trans_details_key: string,
    occurrence: number,
    line_no: string,
    scenario: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=1ydfdj`;
    
    // Build params from all sources with proper fallbacks (matching AngularJS pattern)
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    
    // Add specific parameters
    params.combination_key = rowData.COMBINATION_KEY;
    params.schdd_detail_key = trans_details_key;
    params.occurrence = occurrence;
    params.line_no = line_no;
    params.issue_key = rowData.ISSUE_KEY;
    params.scenario = scenario;

    // Convert params to URLSearchParams for form-urlencoded format
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Save Schedule D details
   * @param baseUrl Base URL for API calls
   * @param final_data Final data array
   * @param clob_settings CLOB settings array
   * @param globalService Global service data
   * @param filterParams Filter parameters
   * @param rowData Row data
   * @returns Observable with response
   */
  saveSchddDetails(
    baseUrl: string,
    final_data: any[],
    clob_settings: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=5gjh3w`;
    
    // Build payload EXACTLY like original AngularJS
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    
    const json_settings = JSON.stringify(clob_settings);
    const json_data = JSON.stringify(final_data);
    
    params = _.extend({ json_data: json_data }, params);
    params = _.extend({ json_settings: json_settings }, params);
    params.process_name = "Schedule-D-Form-save";

    // CRITICAL: Explicitly ensure required parameters are included (matching error message requirements)
    // Tax year
    if (!params.tax_year) {
      params.tax_year = filterParams.tax_year || 
                        rowData.TAX_YEAR || 
                        globalService?.globalParams?.tax_year || 
                        globalService?.tax_year;
    }
    
    // Scenario
    if (!params.scenario) {
      params.scenario = filterParams.scenario || 
                        rowData.SCENARIO || 
                        globalService?.globalParams?.scenario || 
                        globalService?.scenario;
    }
    
    // JCD key
    if (!params.jcd_key) {
      params.jcd_key = filterParams.jcd_key || 
                       rowData.JCD_KEY || 
                       globalService?.globalParams?.jcd_key || 
                       globalService?.jcd_key || 
                       250; // Default fallback as per AngularJS
    }
    
    // Scenario code - ALWAYS set explicitly (required parameter)
    // Use explicit checks to ensure it's always set
    if (params.scenarioCode === undefined || params.scenarioCode === null || params.scenarioCode === '') {
      params.scenarioCode = params.scenario_code || 
                            filterParams.scenarioCode || 
                            filterParams.scenario_code || 
                            rowData.scenarioCode || 
                            rowData.scenario_code || 
                            rowData.SCENARIO_TYPE_CODE ||
                            globalService?.globalParams?.scenarioCode || 
                            globalService?.scenarioCode || 
                            'RAF'; // Default fallback
    }
    
    // Remove scenario_code if scenarioCode is set (avoid duplicates)
    if (params.scenarioCode && params.scenario_code) {
      delete params.scenario_code;
    }
    
    // Is issue key enabled - ALWAYS set explicitly (required parameter)
    // Use explicit checks to ensure it's always set
    if (params.is_issue_key_enabled === undefined || params.is_issue_key_enabled === null || params.is_issue_key_enabled === '') {
      params.is_issue_key_enabled = filterParams.is_issue_key_enabled || 
                                    rowData.is_issue_key_enabled || 
                                    globalService?.globalParams?.is_issue_key_enabled || 
                                    globalService?.is_issue_key_enabled || 
                                    'N'; // Default fallback
    }

    // Build checkLockedbj from rowData (NOT hardcoded)
    const checkLockedbj = [{
      "leid": rowData.LEID,
      "cdr_number": rowData.CDR_NO || rowData.MARS,
      "consol_group_key": rowData.CONSOL_GROUP_KEY,
      "reporting_period": rowData.REPORTING_PERIOD,
      "combination_key": rowData.COMBINATION_KEY,
      "scenario_key": parseInt(params.scenario || "1")
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    // Convert params to URLSearchParams for form-urlencoded format
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });
    
    // CRITICAL: Ensure required parameters are always included (even if empty)
    // Double-check that scenarioCode and is_issue_key_enabled are in the payload
    if (!urlEncoded.has('scenarioCode')) {
      urlEncoded.set('scenarioCode', params.scenarioCode || 'RAF');
    }
    if (!urlEncoded.has('is_issue_key_enabled')) {
      urlEncoded.set('is_issue_key_enabled', params.is_issue_key_enabled || 'N');
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Save Schedule D Reportable Transaction
   * @param baseUrl Base URL for API calls
   * @param data Data object
   * @param repTrans_data Reportable transaction data array
   * @param clob_settings CLOB settings array
   * @param globalService Global service data
   * @param filterParams Filter parameters
   * @returns Observable with response
   */
  saveSchddReportableTransaction(
    baseUrl: string,
    data: any,
    repTrans_data: any[],
    clob_settings: any[],
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=vlae0g`;
    
    // Build payload EXACTLY like original AngularJS
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    
    const json_settings = JSON.stringify(clob_settings);
    const json_data = JSON.stringify(repTrans_data);
    
    params = _.extend({ json_data: json_data }, params);
    params = _.extend({ json_settings: json_settings }, params);
    params.reportable_type = "SCHDD_REPORTABLE_TRANS";
    params.process_name = "Schedule-D-Reportable-Transaction-save";
    params.combination_key = data.COMBINATION_KEY;

    // Convert params to URLSearchParams for form-urlencoded format
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}
